import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { drawerBackground } from '@/theme/colors';
import './DateFilter.css';
import { FiltersCount } from '../styles';
import { InlineCalendar } from './InlineCalendar';
import { IsoDateTextField, isValidIsoDate } from './IsoDateTextField';
import {
  dateFilters as dateFiltersAtom,
  DateFilterValue,
  DateFilterType,
} from '@/components/Analysis/state';

type DateFilterProps = {
  fieldKey: string;
  fieldName: string;
};

export const DateFilter = ({ fieldKey, fieldName }: DateFilterProps) => {
  const [dateFilters, setDateFilters] = useAtom(dateFiltersAtom);
  const currentFilter: DateFilterValue | undefined = dateFilters[fieldKey];
  const filterType: DateFilterType = currentFilter?.type ?? null;
  const startDate = currentFilter?.type === 'range' ? currentFilter.startDate : null;
  const endDate = currentFilter?.type === 'range' ? currentFilter.endDate : null;
  const singleDate = currentFilter?.type === 'single' ? currentFilter.date : null;

  // Treat null filterType as 'range' for display/interaction
  const activeMode: 'range' | 'single' = filterType === 'single' ? 'single' : 'range';

  /** One badge count per field: 1 for a complete single date or complete range, never 2 for range. */
  const filterCount = useMemo(() => {
    if (activeMode === 'single' && singleDate) return 1;
    if (activeMode === 'range' && startDate && endDate) return 1;
    return 0;
  }, [activeMode, singleDate, startDate, endDate]);

  const handleTypeChange = useCallback(
    (newType: DateFilterType) => {
      if (!newType) {
        setDateFilters((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
        return;
      }
      setDateFilters((prev) => ({
        ...prev,
        [fieldKey]:
          newType === 'range'
            ? { type: 'range', startDate: null, endDate: null }
            : { type: 'single', date: null },
      }));
    },
    [fieldKey, setDateFilters]
  );

  const setStartDate = useCallback(
    (value: string | null) => {
      setDateFilters((prev) => ({
        ...prev,
        [fieldKey]: {
          type: 'range',
          startDate: value,
          endDate: prev[fieldKey]?.type === 'range' ? prev[fieldKey].endDate : null,
        },
      }));
    },
    [fieldKey, setDateFilters]
  );

  const setEndDate = useCallback(
    (value: string | null) => {
      setDateFilters((prev) => ({
        ...prev,
        [fieldKey]: {
          type: 'range',
          startDate: prev[fieldKey]?.type === 'range' ? prev[fieldKey].startDate : null,
          endDate: value,
        },
      }));
    },
    [fieldKey, setDateFilters]
  );

  const setSingleDate = useCallback(
    (value: string | null) => {
      setDateFilters((prev) => ({
        ...prev,
        [fieldKey]: { type: 'single', date: value },
      }));
    },
    [fieldKey, setDateFilters]
  );

  const handleClear = useCallback(() => {
    setDateFilters((prev) => ({
      ...prev,
      [fieldKey]:
        activeMode === 'range'
          ? { type: 'range', startDate: null, endDate: null }
          : { type: 'single', date: null },
    }));
  }, [activeMode, fieldKey, setDateFilters]);

  const [draftStart, setDraftStart] = useState(() => startDate ?? '');
  const [draftEnd, setDraftEnd] = useState(() => endDate ?? '');
  const [draftSingle, setDraftSingle] = useState(() => singleDate ?? '');

  useEffect(() => {
    setDraftStart(startDate ?? '');
  }, [startDate]);

  useEffect(() => {
    setDraftEnd(endDate ?? '');
  }, [endDate]);

  useEffect(() => {
    setDraftSingle(singleDate ?? '');
  }, [singleDate]);

  const commitRangeStart = useCallback(
    (newStart: string | null) => {
      setDateFilters((prev) => {
        const cur = prev[fieldKey];
        const end = cur?.type === 'range' ? cur.endDate : null;
        let start = newStart;
        let endFinal = end;
        if (start && endFinal && endFinal < start) {
          [start, endFinal] = [endFinal, start];
        }
        return {
          ...prev,
          [fieldKey]: { type: 'range', startDate: start, endDate: endFinal },
        };
      });
    },
    [fieldKey, setDateFilters]
  );

  const commitRangeEnd = useCallback(
    (newEnd: string | null) => {
      setDateFilters((prev) => {
        const cur = prev[fieldKey];
        const start = cur?.type === 'range' ? cur.startDate : null;
        let startFinal = start;
        let end = newEnd;
        if (startFinal && end && end < startFinal) {
          [startFinal, end] = [end, startFinal];
        }
        return {
          ...prev,
          [fieldKey]: { type: 'range', startDate: startFinal, endDate: end },
        };
      });
    },
    [fieldKey, setDateFilters]
  );

  const handleStartTextChange = useCallback(
    (v: string) => {
      setDraftStart(v);
      if (v === '') {
        commitRangeStart(null);
      } else if (isValidIsoDate(v)) {
        commitRangeStart(v);
      }
    },
    [commitRangeStart]
  );

  const handleEndTextChange = useCallback(
    (v: string) => {
      setDraftEnd(v);
      if (v === '') {
        commitRangeEnd(null);
      } else if (isValidIsoDate(v)) {
        commitRangeEnd(v);
      }
    },
    [commitRangeEnd]
  );

  const handleStartBlur = useCallback(() => {
    if (draftStart === '' || isValidIsoDate(draftStart)) return;
    setDraftStart(startDate ?? '');
  }, [draftStart, startDate]);

  const handleEndBlur = useCallback(() => {
    if (draftEnd === '' || isValidIsoDate(draftEnd)) return;
    setDraftEnd(endDate ?? '');
  }, [draftEnd, endDate]);

  const handleSingleTextChange = useCallback(
    (v: string) => {
      setDraftSingle(v);
      if (v === '') {
        setSingleDate(null);
      } else if (isValidIsoDate(v)) {
        setSingleDate(v);
      }
    },
    [setSingleDate]
  );

  const handleSingleBlur = useCallback(() => {
    if (draftSingle === '' || isValidIsoDate(draftSingle)) return;
    setDraftSingle(singleDate ?? '');
  }, [draftSingle, singleDate]);

  return (
    <Stack>
      <Accordion
        sx={{
          margin: 0,
          boxShadow: 'none',
          backgroundColor: drawerBackground,
          '.Mui-expanded&:before': { opacity: 1 },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          '@media(max-width: 1728px)': {
            '& .MuiAccordionSummary-root': {
              minHeight: '32px',
            },
          },
        }}
        disableGutters
      >
        <AccordionSummary
          sx={{ padding: '0', backgroundColor: drawerBackground }}
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="date-filter-content"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 500, paddingLeft: '1rem' }} variant="body2">
              {`Select ${fieldName}`}
            </Typography>
            {filterCount > 0 && <FiltersCount>{filterCount}</FiltersCount>}
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ margin: 0, px: 1 }}>
          <RadioGroup
            row
            value={activeMode}
            onChange={(e) => handleTypeChange(e.target.value as DateFilterType)}
          >
            <FormControlLabel
              value="range"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Date Range</Typography>}
            />
            <FormControlLabel
              value="single"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Single Date</Typography>}
            />
          </RadioGroup>

          {activeMode === 'range' && (
            <Stack direction="column" gap={1.5} sx={{ my: 1 }}>
              <IsoDateTextField
                id={`date-filter-${fieldKey}-start`}
                label="Start date"
                value={draftStart}
                onChange={handleStartTextChange}
                onBlur={handleStartBlur}
                sx={{ m: 0 }}
                aria-label="Start date YYYY-MM-DD"
              />
              <IsoDateTextField
                id={`date-filter-${fieldKey}-end`}
                label="End date"
                value={draftEnd}
                onChange={handleEndTextChange}
                onBlur={handleEndBlur}
                sx={{ m: 0 }}
                aria-label="End date YYYY-MM-DD"
              />
            </Stack>
          )}

          {activeMode === 'single' && (
            <Stack direction="column" gap={1.5} sx={{ my: 1 }}>
              <IsoDateTextField
                id={`date-filter-${fieldKey}-single-start`}
                label="Start date"
                value={draftSingle}
                onChange={handleSingleTextChange}
                onBlur={handleSingleBlur}
                sx={{ m: 0 }}
                aria-label="Start date YYYY-MM-DD"
              />
            </Stack>
          )}

          <InlineCalendar
            mode={activeMode}
            startDate={startDate}
            endDate={endDate}
            singleDate={singleDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
            onSingleChange={setSingleDate}
            onClear={handleClear}
          />
        </AccordionDetails>
      </Accordion>
      <Divider />
    </Stack>
  );
};
